/**
 * Customer 360 financial view (Phase 4). The server's `financial` contract says where the numbers come
 * from: `LEDGER` → the money tabs load `/users/:id/finance` (ledger balances, holds, adjustments,
 * deposits, withdrawals, journal — all PostgreSQL truth, decimal strings); `NONE` → no money tabs.
 * `?fixture=rich` (development builds only) still loads the labelled REVIEW fixture used to design
 * the later-phase crypto/trade tabs; the loader is `null` in production and never bundled
 * (scripts/check-prod-bundles.ts). Nothing here computes money: rows are server values re-labelled.
 */
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { CustomerFinancial, CustomerLedger } from '../api.ts';
import type { CustomerFinanceView, TxKind, TxRow, WalletRow } from './finance-model.ts';
import { ADJ_OP, DEPOSIT_STATUS, unitOf, WITHDRAWAL_STATUS } from './money-labels.ts';
import { useRemote } from './remote.tsx';

export type { CustomerFinanceView, TxKind, TxRow, WalletRow } from './finance-model.ts';

const loadFixture: ((name: string) => Promise<CustomerFinanceView | null>) | null = import.meta.env
  .DEV
  ? (name) => import('./fixtures.ts').then((m) => m.ADMIN_FIXTURES[name] ?? null)
  : null;

export const TX_KIND: Record<TxKind, string> = {
  TOMAN_DEPOSIT: 'واریز تومان',
  TOMAN_WITHDRAWAL: 'برداشت تومان',
  CRYPTO_DEPOSIT: 'واریز رمزارز',
  CRYPTO_WITHDRAWAL: 'برداشت رمزارز',
  BUY: 'خرید',
  SELL: 'فروش',
  CONVERT: 'تبدیل',
  ADMIN_ADJUSTMENT: 'اصلاحیه‌ی مدیر',
};

const DEP: Record<string, TxRow['status']> = {
  SUBMITTED: 'pending',
  UNDER_REVIEW: 'pending',
  APPROVED_CREDITED: 'completed',
  REJECTED: 'rejected',
  CANCELLED: 'refunded',
};
const WD: Record<string, TxRow['status']> = {
  PROCESSING: 'processing',
  SECURITY_HOLD: 'hold',
  SETTLEMENT_UNCERTAIN: 'hold',
  COMPLETED: 'completed',
  REJECTED_REFUNDED: 'refunded',
};

/** Server ledger read model → the Customer 360 table rows (labels only; amounts untouched). */
export function ledgerView(financial: CustomerFinancial, l: CustomerLedger): CustomerFinanceView {
  const wallets: WalletRow[] = l.balances.map((b) => ({
    id: b.asset,
    asset: unitOf(b.asset),
    total: b.total,
    available: b.available,
    reserved: b.reserved,
    tomanValue: b.asset === 'IRT' ? b.total : null,
  }));
  const tx: TxRow[] = [
    ...l.deposits.map((d) => ({
      id: d.id,
      type: 'TOMAN_DEPOSIT' as const,
      asset: 'TOMAN',
      amount: d.finalAmount ?? d.declaredAmount,
      tomanValue: d.finalAmount ?? d.declaredAmount,
      fee: null,
      status: DEP[d.status] ?? 'pending',
      statusLabel: DEPOSIT_STATUS[d.status]?.label ?? d.status,
      network: null,
      reference: d.ref,
      at: d.createdAt,
    })),
    ...l.withdrawals.map((w) => ({
      id: w.id,
      type: 'TOMAN_WITHDRAWAL' as const,
      asset: 'TOMAN',
      amount: w.amount,
      tomanValue: w.amount,
      fee: w.fee,
      status: WD[w.status] ?? 'processing',
      statusLabel: WITHDRAWAL_STATUS[w.status]?.label ?? w.status,
      network: null,
      reference: w.ref,
      at: w.createdAt,
    })),
    ...l.adjustments.map((a) => ({
      id: a.id,
      type: 'ADMIN_ADJUSTMENT' as const,
      asset: unitOf(a.asset),
      amount: a.amount,
      tomanValue: a.asset === 'IRT' ? a.amount : null,
      fee: null,
      status: 'completed' as const,
      statusLabel: ADJ_OP[a.operation] ?? a.operation,
      network: null,
      reference: a.ref,
      at: a.createdAt,
    })),
  ].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  return { financial, wallets, transactions: tx, riskFlags: [], ledger: l };
}

export interface CustomerFinanceState extends CustomerFinanceView {
  /** Set when the ledger read model failed to load — never rendered as zero or as empty. */
  error: string | null;
  /** A dev-only review fixture was requested (`?fixture=`). */
  fixture: boolean;
  reload: () => void;
}

export function useCustomerFinance(
  userId: string,
  financial: CustomerFinancial,
): CustomerFinanceState {
  const search = useRouterState({ select: (s) => s.location.searchStr });
  const fixture = loadFixture ? new URLSearchParams(search).get('fixture') : null;
  const [fx, setFx] = useState<{ name: string; view: CustomerFinanceView | null } | null>(null);
  useEffect(() => {
    if (!fixture || !loadFixture) return;
    let live = true;
    void loadFixture(fixture).then((view) => live && setFx({ name: fixture, view }));
    return () => {
      live = false;
    };
  }, [fixture]);
  const ledger = useRemote<CustomerLedger>(
    !fixture && financial.source === 'LEDGER' ? `/users/${userId}/finance` : null,
  );
  return useMemo(() => {
    const none = { financial, wallets: null, transactions: null, riskFlags: [], ledger: null };
    if (fixture) {
      const v = fx && fx.name === fixture ? fx.view : null;
      return { ...(v ?? none), error: null, fixture: true, reload: () => {} };
    }
    if (ledger.data)
      return {
        ...ledgerView(financial, ledger.data),
        error: null,
        fixture: false,
        reload: ledger.reload,
      };
    return { ...none, error: ledger.error, fixture: false, reload: ledger.reload };
  }, [fixture, fx, financial, ledger.data, ledger.error, ledger.reload]);
}
